import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { themeable } from '@instructure/ui-themeable';
import { ms } from '@instructure/ui-utils';
import { testable } from '@instructure/ui-testable';
import { BaseTransition } from './BaseTransition';
var styles = {
  componentId: 'eJkkQ',
  template: function template(theme) {
    return "\n\n.eJkkQ_bGBk{transform:translateZ(0)}\n\n.eJkkQ_ddDm{transition:opacity ".concat(theme.duration || 'inherit', " ").concat(theme.timing || 'inherit', ",transform ").concat(theme.duration || 'inherit', " ").concat(theme.timing || 'inherit', "}\n\n.eJkkQ_cpZA,.eJkkQ_fTmM{opacity:0.01}\n\n.eJkkQ_bEpV,.eJkkQ_NoZX{opacity:1}\n\n.eJkkQ_fOgU{transition:opacity ").concat(theme.duration || 'inherit', " ").concat(theme.timing || 'inherit', ",transform ").concat(theme.duration || 'inherit', " ").concat(theme.timing || 'inherit', "}\n\n.eJkkQ_ZDum,.eJkkQ_fihg{opacity:0.01;transform:scale(0.01) translateZ(0)}\n\n.eJkkQ_eqrf,.eJkkQ_ftWB{opacity:1;transform:scale(1) translateZ(0)}\n\n.eJkkQ_dlJm,.eJkkQ_cPWt,.eJkkQ_bewW,.eJkkQ_bHhn{transition:opacity ").concat(theme.duration || 'inherit', " ").concat(theme.timing || 'inherit', ",transform ").concat(theme.duration || 'inherit', " ").concat(theme.timing || 'inherit', "}\n\n.eJkkQ_bOBe,.eJkkQ_eKZC,.eJkkQ_cusP,.eJkkQ_djUe{opacity:0.01}\n\n.eJkkQ_cusP,.eJkkQ_dWfS{transform:translate3d(100%,0,0)}\n\n.eJkkQ_eKZC,.eJkkQ_chud{transform:translate3d(-100%,0,0)}\n\n.eJkkQ_djUe,.eJkkQ_fDdb{transform:translate3d(0,-100%,0)}\n\n.eJkkQ_bOBe,.eJkkQ_cYey{transform:translate3d(0,100%,0)}\n\n.eJkkQ_diPG,.eJkkQ_fEMB,.eJkkQ_eOlt,.eJkkQ_fvoZ,.eJkkQ_bIuM,.eJkkQ_fyZW,.eJkkQ_cuyj,.eJkkQ_gaix{opacity:1;transform:translateZ(0)}");
  },
  'root': 'eJkkQ_bGBk',
  'fade': 'eJkkQ_ddDm',
  'fade--exited': 'eJkkQ_cpZA',
  'fade--exiting': 'eJkkQ_fTmM',
  'fade--entered': 'eJkkQ_bEpV',
  'fade--entering': 'eJkkQ_NoZX',
  'scale': 'eJkkQ_fOgU',
  'scale--exited': 'eJkkQ_ZDum',
  'scale--exiting': 'eJkkQ_fihg',
  'scale--entered': 'eJkkQ_eqrf',
  'scale--entering': 'eJkkQ_ftWB',
  'slide-down': 'eJkkQ_dlJm',
  'slide-left': 'eJkkQ_cPWt',
  'slide-right': 'eJkkQ_bewW',
  'slide-up': 'eJkkQ_bHhn',
  'slide-down--exited': 'eJkkQ_bOBe',
  'slide-left--exited': 'eJkkQ_eKZC',
  'slide-right--exited': 'eJkkQ_cusP',
  'slide-up--exited': 'eJkkQ_djUe',
  'slide-right--exiting': 'eJkkQ_dWfS',
  'slide-left--exiting': 'eJkkQ_chud',
  'slide-up--exiting': 'eJkkQ_fDdb',
  'slide-down--exiting': 'eJkkQ_cYey',
  'slide-down--entered': 'eJkkQ_diPG',
  'slide-down--entering': 'eJkkQ_fEMB',
  'slide-left--entered': 'eJkkQ_eOlt',
  'slide-left--entering': 'eJkkQ_fvoZ',
  'slide-right--entered': 'eJkkQ_bIuM',
  'slide-right--entering': 'eJkkQ_fyZW',
  'slide-up--entered': 'eJkkQ_cuyj',
  'slide-up--entering': 'eJkkQ_gaix'
};
import theme from './theme';
/**
---
category: components/utilities
---
@module Transition
**/

var Transition = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 =
/*#__PURE__*/
function (_Component) {
  _inherits(Transition, _Component);

  function Transition() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, Transition);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(Transition)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _this.handleExited = function () {
      if (typeof _this.props.onExited === 'function') {
        _this.props.onExited(_this.props.type);
      }
    };

    _this.handleEntered = function () {
      if (typeof _this.props.onEntered === 'function') {
        _this.props.onEntered(_this.props.type);
      }
    };

    return _this;
  }

  _createClass(Transition, [{
    key: "render",
    value: function render() {
      var _this$props = this.props,
          type = _this$props.type,
          children = _this$props.children,
          props = _objectWithoutProperties(_this$props, ["type", "children"]);

      var duration = ms(this.theme.duration);
      var classNames = type ? {
        'exited': styles["".concat(type, "--exited")],
        'exiting': styles["".concat(type, "--exiting")],
        'entering': styles["".concat(type, "--entered")],
        'entered': styles["".concat(type, "--entering")]
      } : {};
      return React.createElement(BaseTransition, Object.assign({}, props, {
        enterDelay: duration,
        exitDelay: duration,
        transitionClassName: styles[type],
        exitedClassName: classNames.exited,
        exitingClassName: classNames.exiting,
        enteredClassName: classNames.entering,
        enteringClassName: classNames.entered,
        onEntered: this.handleEntered,
        onExited: this.handleExited
      }), children);
    }
  }]);

  Transition.displayName = "Transition";
  return Transition;
}(Component), _class2.propTypes = {
  type: PropTypes.oneOf(['fade', 'scale', 'slide-down', 'slide-up', 'slide-left', 'slide-right']),

  /**
   * A single element to animate in and out
   */
  children: PropTypes.element,

  /**
   * Show the component; triggers the enter or exit animation
   */
  in: PropTypes.bool,

  /**
   * Unmount the component (remove it from the DOM) when it is not shown
   */
  unmountOnExit: PropTypes.bool,

  /**
   * Run the enter animation when the component mounts, if it is initially
   * shown
   */
  transitionOnMount: PropTypes.bool,

  /**
   * Run the enter animation
   */
  transitionEnter: PropTypes.bool,

  /**
   * Run the exit animation
   */
  transitionExit: PropTypes.bool,

  /**
   * Callback fired when transitioning to the next state
   */
  onTransition: PropTypes.func,

  /**
   * Callback fired before the "entering" classes are applied
   */
  onEnter: PropTypes.func,

  /**
   * Callback fired after the "entering" classes are applied
   */
  onEntering: PropTypes.func,

  /**
   * Callback fired after the "enter" classes are applied
   */
  onEntered: PropTypes.func,

  /**
   * Callback fired before the "exiting" classes are applied
   */
  onExit: PropTypes.func,

  /**
   * Callback fired after the "exiting" classes are applied
   */
  onExiting: PropTypes.func,

  /**
   * Callback fired after the "exited" classes are applied
   */
  onExited: PropTypes.func
}, _class2.defaultProps = {
  type: 'fade',
  in: false,
  unmountOnExit: false,
  transitionOnMount: false,
  transitionEnter: true,
  transitionExit: true,
  onEnter: function onEnter() {},
  onEntering: function onEntering() {},
  onEntered: function onEntered() {},
  onExit: function onExit() {},
  onExiting: function onExiting() {},
  onExited: function onExited() {},
  onTransition: function onTransition(toState, fromState) {},
  children: null
}, _class2.states = BaseTransition.states, _temp)) || _class) || _class);
export default Transition;
export { Transition };